import { Mail, PhoneCall, MapPin, ArrowUpRight, Linkedin, Facebook, Instagram, Twitter } from "lucide-react";

export default function Footer() {
  const go = (id) => document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <footer data-testid="footer-main" className="bg-[#0A1A3F] text-slate-300">
      <div className="mx-auto grid max-w-7xl gap-12 px-4 py-16 sm:px-6 lg:grid-cols-[1.3fr_1fr_1fr_1.2fr]">
        <div>
          <div data-testid="footer-brand-title" className="inline-flex rounded-2xl bg-white p-3">
            <img
              src="/assets/logo.png"
              alt="Impact Sutra — From Intent to Impact"
              className="h-16 w-auto"
            />
          </div>
          <p className="mt-4 max-w-sm text-sm leading-relaxed text-slate-400">
            A specialist CSR fundraising consultancy bridging grassroots NGOs with corporate social
            responsibility capital — from compliance packaging to grant disbursement and renewal.
          </p>
          <div className="mt-6 flex items-center gap-3">
            {[
              { icon: Linkedin, href: "https://www.linkedin.com/company/impact-sutra", label: "LinkedIn", testId: "social-linkedin" },
              { icon: Facebook, href: "https://www.facebook.com/impactsutra", label: "Facebook", testId: "social-facebook" },
              { icon: Instagram, href: "https://www.instagram.com/impactsutra", label: "Instagram", testId: "social-instagram" },
              { icon: Twitter, href: "https://x.com/impactsutra", label: "X (Twitter)", testId: "social-twitter" },
            ].map((s) => (
              <a
                key={s.testId}
                data-testid={s.testId}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-white/15 bg-white/5 text-slate-300 transition-all hover:-translate-y-0.5 hover:border-[#E8862A] hover:bg-[#E8862A] hover:text-white"
              >
                <s.icon className="h-4 w-4" />
              </a>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Explore</p>
          <ul className="mt-4 space-y-2.5 text-sm">
            {[
              ["services", "About & Services"],
              ["team", "Leadership"],
              ["work", "Past Impact"],
              ["plans", "Association Plans"],
              ["calculator", "Funding Estimator"],
              ["faq", "FAQ"],
            ].map(([id, label]) => (
              <li key={id}>
                <button
                  data-testid={`footer-link-${id}`}
                  onClick={() => go(id)}
                  className="inline-flex items-center gap-1 text-slate-400 transition-colors hover:text-white"
                >
                  {label}
                  <ArrowUpRight className="h-3.5 w-3.5" />
                </button>
              </li>
            ))}
          </ul>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">For NGOs</p>
          <ul className="mt-4 space-y-2.5 text-sm text-slate-400">
            <li>Free eligibility audit</li>
            <li>CSR-1 / 80G / 12A packaging</li>
            <li>Corporate pitch decks</li>
            <li>Grant M&E reporting</li>
          </ul>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Contact</p>
          <ul className="mt-4 space-y-3.5 text-sm">
            <li>
              <a data-testid="footer-contact-email" href="mailto:director@impactsutra.in" className="inline-flex items-center gap-2.5 text-slate-300 transition-colors hover:text-white">
                <Mail className="h-4 w-4 text-[#E8862A]" />
                director@impactsutra.in
                <span className="text-xs text-slate-500">(Director)</span>
              </a>
            </li>
            <li>
              <a data-testid="footer-contact-email-info" href="mailto:info@impactsutra.in" className="inline-flex items-center gap-2.5 text-slate-300 transition-colors hover:text-white">
                <Mail className="h-4 w-4 text-[#E8862A]" />
                info@impactsutra.in
                <span className="text-xs text-slate-500">(General Enquiries)</span>
              </a>
            </li>
            <li>
              <a data-testid="footer-contact-phone" href="tel:+917906028651" className="inline-flex items-center gap-2.5 text-slate-300 transition-colors hover:text-white">
                <PhoneCall className="h-4 w-4 text-[#E8862A]" />
                +91 79060 28651 (Advisory Hotline)
              </a>
            </li>
            <li data-testid="footer-office-address" className="flex items-start gap-2.5 text-slate-400">
              <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-[#E8862A]" />
              <span>
                #402, Maruthi Towers, 1569, Trichy Road,
                <br />
                opp. Government Hospital, Highways Colony,
                <br />
                Gopalapuram, Coimbatore, Tamil Nadu 641018
              </span>
            </li>
          </ul>
        </div>
      </div>

      <div className="border-t border-white/10">
        <div className="mx-auto flex max-w-7xl flex-col gap-3 px-4 py-6 text-xs text-slate-500 sm:px-6 lg:flex-row lg:items-center lg:justify-between">
          <p data-testid="footer-csr-disclaimer" className="max-w-2xl leading-relaxed">
            Impact Sutra is a fundraising advisory and does not guarantee grant approvals.
            All CSR funding is subject to corporate due diligence under Section 135 of the Companies
            Act, 2013 and Schedule VII guidelines.
          </p>
          <p>© {new Date().getFullYear()} Impact Sutra. All rights reserved.</p>
        </div>
      </div>
    </footer>
  );
}
